import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useT } from '../../i18n/LanguageContext';
import { isSessionCurrent, SESSION_EVENT } from '../../utils/authSession';
import { getPendingFeedback, finishFeedback } from '../../utils/feedbackEvents';
import service from '../../services/feedbackService';
import ResourceDialog from '../resources/ResourceDialog';
import { feedbackContextBusy } from '../../features/feedback/promptContext';
import './feedback.css';
export { feedbackContextBusy } from '../../features/feedback/promptContext';
export default function WeeklyFeedback() {
  const { user, sessionVersion } = useAuth();
  return user?.isActive !== false && ['clinic', 'sales_rep'].includes(user?.role) ? (
    <FeedbackSession key={`${user._id || user.id}:${sessionVersion || ''}`} />
  ) : null;
}
function FeedbackSession() {
  const t = useT();
  const [prompt, setPrompt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const alive = useRef(true);
  const promptRef = useRef(null);
  const response = useRef(null);
  const submitting = useRef(false);
  useEffect(() => {
    alive.current = true;
    let claiming = false;
    let lastInteraction = Date.now();
    const interaction = () => {
      lastInteraction = Date.now();
    };
    const changed = () => {
      const entry = promptRef.current;
      if (entry && !isSessionCurrent(entry.session)) {
        promptRef.current = null;
        setPrompt(null);
      }
      getPendingFeedback();
    };
    const tick = async () => {
      const entry = getPendingFeedback();
      if (
        !entry ||
        claiming ||
        submitting.current ||
        promptRef.current ||
        Date.now() < entry.readyAt ||
        Date.now() - lastInteraction < 4000 ||
        feedbackContextBusy()
      )
        return;
      claiming = true;
      try {
        const result = await service.claim(entry.operationId);
        if (!isSessionCurrent(entry.session)) return;
        // Claim reserves the shared seven-day cooldown. Never interrupt a task that began while waiting.
        if (!result.eligible) {
          finishFeedback(entry, result.nextEligibleAt);
          return;
        }
        const next = { ...entry, promptId: result.promptId, nextEligibleAt: result.nextEligibleAt };
        finishFeedback(entry, result.nextEligibleAt);
        if (
          !alive.current ||
          Date.now() > entry.expiresAt ||
          window.location.pathname !== entry.path ||
          feedbackContextBusy() ||
          Date.now() - lastInteraction < 4000
        )
          return;
        response.current = null;
        setError(false);
        setBusy(false);
        promptRef.current = next;
        setPrompt(next);
      } catch {
        // A lost claim response may already have reserved the cooldown. Retry its UUID, never a new claim.
        entry.attempts = (entry.attempts || 0) + 1;
        if (entry.attempts < 3) entry.readyAt = Date.now() + 30000;
        else finishFeedback(entry);
      } finally {
        claiming = false;
      }
    };
    const timer = setInterval(tick, 1000);
    ['pointerdown', 'keydown', 'input'].forEach((name) =>
      document.addEventListener(name, interaction, true)
    );
    window.addEventListener(SESSION_EVENT, changed);
    window.addEventListener('storage', changed);
    return () => {
      alive.current = false;
      clearInterval(timer);
      ['pointerdown', 'keydown', 'input'].forEach((name) =>
        document.removeEventListener(name, interaction, true)
      );
      window.removeEventListener(SESSION_EVENT, changed);
      window.removeEventListener('storage', changed);
    };
  }, []);
  const close = () => {
    const entry = promptRef.current;
    if (!entry) return;
    // Do not replace an uncertain up/down response with a different answer.
    if (!response.current && isSessionCurrent(entry.session))
      service.respond(entry.promptId, 'dismiss').catch(() => {});
    promptRef.current = null;
    setPrompt(null);
  };
  const submit = async (choice) => {
    if (submitting.current || !promptRef.current) return;
    const entry = promptRef.current;
    if (!isSessionCurrent(entry.session)) {
      setPrompt(null);
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError(false);
    response.current = response.current || choice;
    try {
      await service.respond(entry.promptId, response.current);
      if (alive.current && promptRef.current === entry && isSessionCurrent(entry.session)) {
        promptRef.current = null;
        setPrompt(null);
      }
    } catch {
      if (alive.current && promptRef.current === entry && isSessionCurrent(entry.session))
        setError(true);
    } finally {
      submitting.current = false;
      if (alive.current) setBusy(false);
    }
  };
  if (!prompt) return null;
  return (
    <div className="me-feedback">
      <ResourceDialog title={t('Enjoying the MedEffects App?')} onClose={close}>
        <div className="me-feedback-body">
          <div className="me-feedback-options">
            <button
              type="button"
              className="me-feedback-up"
              disabled={busy || !!response.current}
              onClick={() => submit('up')}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                thumb_up
              </span>
              {t('Yes, I am')}
            </button>
            <button
              type="button"
              className="me-feedback-down"
              disabled={busy || !!response.current}
              onClick={() => submit('down')}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                thumb_down
              </span>
              {t('Could be better')}
            </button>
          </div>
          {error && (
            <div role="alert">
              <p>{t('Could not save your feedback. Please retry.')}</p>
              <button type="button" disabled={busy} onClick={() => submit(response.current)}>
                {t('Retry')}
              </button>
            </div>
          )}
        </div>
      </ResourceDialog>
    </div>
  );
}
